import React, { useMemo } from "react";
import cmsAdminM from "assets/images/cmsAdmin.png";
import cmsAdminF from "assets/images/cmsAdmin_f.png";
import managerM from "assets/images/manager.png";
import managerF from "assets/images/manager_f.png";
import safetyAdminM from "assets/images/safetyAdmin.png";
import safetyAdminF from "assets/images/safetyAdmin_f.png";
import siteAdminM from "assets/images/siteAdmin.png";
import siteAdminF from "assets/images/siteAdmin_f.png";
import unknown from "assets/images/unknow.png";
import workerM from "assets/images/worker.png";
import workerF from "assets/images/worker_f.png";
import { getFullName, getMultiLangProperty } from "base/BaseHelper";
import { FlexDiv, StyledP } from "base/BaseStyle";
import { useTranslation } from "react-i18next";
import { DEFAULT_ROLES, USER_BLACKLIST_STATUS } from "constants/base";
import { LEVELS } from "constants/permissions";
import white from "assets/images/white.jpg";
import StatusTag from "../StatusTag";
import { IconContainer, CustomStyledP, LogoContainer } from "./Avatar.styles";

const iconList = {
  [DEFAULT_ROLES.MANAGER]: {
    f: managerF,
    m: managerM,
  },
  [DEFAULT_ROLES.SAFETY_ADMIN]: {
    f: safetyAdminF,
    m: safetyAdminM,
  },
  [DEFAULT_ROLES.SITE_ADMIN]: {
    f: siteAdminF,
    m: siteAdminM,
  },
  [DEFAULT_ROLES.WORKER]: {
    f: workerF,
    m: workerM,
  },
  [DEFAULT_ROLES.CMS_ADMIN]: {
    f: cmsAdminF,
    m: cmsAdminM,
  },
};

function Avatar({
  user = {},
  showPhone = true,
  showStatus,
  sm,
  displayRole,
  isSystemUser,
  relatedSite,
}) {
  const { t, i18n } = useTranslation();
  const {
    role,
    phone,
    status,
    gender = "f",
    UserGroup,
    Company,
    profiles,
    blacklistStatus,
  } = user || {};

  const SubCon = useMemo(() => {
    if (relatedSite) {
      const { SubContractor } = profiles.find(
        (item) =>
          item?.ConstructionSite === relatedSite ||
          item?.ConstructionSite?._id === relatedSite,
      );

      if (SubContractor?._id) {
        return getMultiLangProperty(SubContractor?.name, i18n.language);
      }

      return "";
    }

    return "";
  }, [relatedSite, profiles, i18n.language]);

  const icon = useMemo(() => {
    if (isSystemUser) {
      const { level } = UserGroup || {};

      if (level === LEVELS.SITE) {
        const jointVentureSite = profiles.find(
          (profile) => profile.ConstructionSite.isJointVenture,
        )?.ConstructionSite;

        return (
          jointVentureSite?.logo?.fileUrl || Company?.Logo?.fileUrl || white
        );
      }

      if (level === LEVELS.COMPANY && Company?.Logo) {
        return Company?.Logo?.fileUrl;
      }

      return white;
    }

    if (role && iconList?.[role]) {
      return gender ? iconList?.[role]?.[gender] : iconList?.[role].m;
    }

    return unknown;
  }, [isSystemUser, role, UserGroup, Company?.Logo, profiles, gender]);

  function cleanUserGroup(isDisplayRole) {
    if (isDisplayRole) {
      return getMultiLangProperty(displayRole?.name, i18n.language);
    }

    return getMultiLangProperty(UserGroup?.name, i18n.language);
  }

  return (
    <FlexDiv $gap={5} style={{ width: "100%" }}>
      <FlexDiv $gap={sm ? 10 : 20} style={{ width: "100%" }}>
        {isSystemUser ? (
          <LogoContainer $sm={sm}>
            <img src={icon} alt="account" />
          </LogoContainer>
        ) : (
          <IconContainer $sm={sm}>
            <img src={icon} alt="account" />
          </IconContainer>
        )}

        <FlexDiv
          direction="column"
          $alignItems="flex-start"
          style={{ flex: "1 0 auto", width: 0 }}
        >
          {blacklistStatus === USER_BLACKLIST_STATUS.BLACKLISTED && (
            <CustomStyledP
              fontSize={sm ? 13 : 18}
              color="danger"
              $fontWeight="600"
            >
              {t(`Users.${blacklistStatus}`)}
            </CustomStyledP>
          )}

          <FlexDiv direction="column" $alignItems="flex-start">
            {isSystemUser ? (
              <CustomStyledP
                fontSize={sm ? 13 : 18}
                $fontWeight="500"
                color="primary"
                $sm={sm}
              >
                {getMultiLangProperty(UserGroup?.name, "en")}
              </CustomStyledP>
            ) : (
              <CustomStyledP
                fontSize={sm ? 13 : 18}
                $fontWeight="500"
                color="primary"
                $sm={sm}
              >
                {UserGroup?._id ? cleanUserGroup() : ""}
                {displayRole && ` (${cleanUserGroup(true)})`}
              </CustomStyledP>
            )}

            <CustomStyledP fontSize={sm ? 15 : 18} $fontWeight="500" $sm>
              {getFullName(user, i18n.language)}
            </CustomStyledP>
          </FlexDiv>

          {isSystemUser && Company?._id && (
            <CustomStyledP
              fontSize={sm ? 14 : 16}
              color={sm ? "gray" : "black"}
            >
              {getMultiLangProperty(Company?.name, i18n.language)}
            </CustomStyledP>
          )}

          {!isSystemUser && SubCon && (
            <CustomStyledP
              fontSize={sm ? 14 : 16}
              color={sm ? "gray" : "black"}
            >
              {SubCon}
            </CustomStyledP>
          )}

          {showPhone && (
            <StyledP fontSize={sm ? 14 : 16} color="gray">
              {phone?.number}
            </StyledP>
          )}
        </FlexDiv>
      </FlexDiv>
      {showStatus && status && <StatusTag status={status} />}
    </FlexDiv>
  );
}

export default Avatar;
